import {AnyAttrs, AttrsSchema, Component} from 'panel';
import uniqBy from 'lodash/uniqBy';

import {Persistence} from 'common/report/persistence';
import {dataGroupsClient} from 'common/report/queries/app-api/data-groups-client';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {renameProperty} from 'common/report/util/properties';
import {reporter} from 'common/report/error-reporting';
import {fuzzyMatch} from 'common/util/fuzzy-match';

import {TimeUnitLabels} from '../date-range-picker/models/types';
import template from './child-menu-items-menu.jade';

export interface TooltipContextActionResult {
  handled: boolean;
  openedModal: boolean;
}

export interface BaseState {
  searchFilter?: string;
  projectId?: number;
  staged?: any;
  selected?: any;
  keydownProxyEl?: HTMLElement;
  workspaceId?: number;
}

interface ChildMenuBaseAttrs {
  'all-data-groups'?: boolean;
  'data-group-id'?: string;
  'event-names'?: string[];
  'exclude-autotrack-events'?: boolean;
  'exclude-custom-events'?: boolean;
  'exclude-dimension-properties'?: boolean;
  'excluded-elements'?: any[];
  'max-data-history-days'?: number;
  multiple?: boolean;
  'project-id'?: number;
  'hide-events'?: boolean;
  'project-has-people'?: boolean;
  'project-has-cohorts'?: boolean;
  'filter-by-types'?: any[];
  'show-special-date-property'?: boolean;
  'hide-special-date-property-unit'?: boolean;
  'show-special-dataset-property'?: boolean;
  open?: boolean;
  'search-filter'?: string;
  selected?: any;
  tabs?: any;
  'user-id'?: number;
  'inline-cohorts-override'?: boolean;
  'support-b2b-cohorts'?: boolean;
  'single-data-group'?: string;
  'hide-edit-controls'?: boolean;
  'workspace-id'?: number;
  'track-name'?: string;
}

// Superclass for components rendered in mp-query-block's mp-drop-menu
export class ChildMenu<StateT, AttrsT = AnyAttrs> extends Component<BaseState & StateT, ChildMenuBaseAttrs & AttrsT> {
  private persistence: Persistence;
  private itemsMenuEl: any;

  static get attrsSchema(): AttrsSchema<ChildMenuBaseAttrs> {
    return {
      ...super.attrsSchema,
      'all-data-groups': {
        type: `boolean`,
        description: `enables data groups property selection`,
      },
      'data-group-id': {
        type: `string`,
        description: `defines the selected data group id`,
      },
      'event-names': {
        type: `json`,
        default: null,
        description: `when set, displayed properties belong only to the set of events passed`,
      },
      'exclude-autotrack-events': {
        type: `boolean`,
        description: `whether to hide autotrack events`,
      },
      'exclude-custom-events': {
        type: `boolean`,
        description: `wheter to hide custom events from event selection menus`,
      },
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'excluded-elements': {
        type: `json`,
        default: [],
        description: `defines items to be hidden from selection menus`,
      },
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      multiple: {
        type: `boolean`,
        description: `allows multi selection for selection menus`,
      },
      'project-id': `number`,
      'hide-events': {
        type: `boolean`,
        description: `when false, disables event property selection`,
      },
      'project-has-people': {
        type: `boolean`,
        description: `enables people properties selection`,
      },
      'project-has-cohorts': {
        type: `boolean`,
        description: `enables cohorts selection`,
      },
      'filter-by-types': {
        type: `json`,
        description: `Array of property item types to filter all items by`,
      },
      'show-special-date-property': {
        type: `boolean`,
        description: `enables special date property and breakdown type for datetime`,
      },
      'hide-special-date-property-unit': {
        type: `boolean`,
        description: `disables the unit selection for special date property break-down`,
      },
      'show-special-dataset-property': {
        type: `boolean`,
        description: `enables segmentation and filtering by dataset for rollup projects`,
      },
      open: `boolean`,
      'search-filter': `string`,
      selected: {type: `json`, description: `selection object (varies by menu type)`},
      tabs: `json`,
      'user-id': `number`,
      'inline-cohorts-override': {
        type: `boolean`,
        description: `override cohorts access and allow inline cohorts`,
      },
      'support-b2b-cohorts': {
        type: `boolean`,
        description: `whether b2b cohorts are enabled`,
      },
      'single-data-group': {
        type: `string`,
        description: `if set, shows group properties for a specific data group id`,
      },
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'workspace-id': {
        type: `number`,
        description: `workspace to query for. if not provided, fall back to project queries`,
      },
      'track-name': `string`,
    };
  }

  get config() {
    return {
      template,

      helpers: {
        propagateEvent: (ev) => propagateEvent(this, ev),
        handleItemsMenuInsert: ({elm}) => (this.itemsMenuEl = elm),
        customSearchMatcherFn: (item, searchString) => {
          return fuzzyMatch(item.searchableContent, `"${searchString}"`);
        },
        getSearchFilter: (): string => this.state.searchFilter || this.attr(`search-filter`),
      },
    };
  }

  connectedCallback() {
    this.createCache();
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.itemsMenuEl = null;
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    // prevent initialization race conditions by generating
    // a new cache whenever the namespace changes on menu open
    switch (attr) {
      case `user-id`:
      case `project-id`:
      case `workspace-id`:
        this.createCache();
        break;
      case `open`:
        if (this.itemsMenuEl && this.attr(`open`)) {
          requestAnimationFrame(() => this.itemsMenuEl.focusSelectedItem());
        }
        break;
    }
  }

  public createCache() {
    if (this.attr(`user-id`) && (this.attr(`project-id`) || this.attr(`workspace-id`))) {
      this.persistence = Persistence.createCachedNamespace(
        // prefix the workspace so there are no collisions with project ids
        this.attr(`workspace-id`) ? `wksp-${this.attr(`workspace-id`)}` : this.projectId,
        this.attr(`user-id`),
        this.getPersistenceKey(),
      );
    }
  }

  public clearCache() {
    if (this.persistence) {
      this.persistence.clearCachedNamespace();
      this.persistence.clear(this.getPersistenceKey());
    }
  }

  // Keeps storage of items light
  public getMaxRecentItems() {
    return 4;
  }

  public getPersistenceKey() {
    return `child-menu`;
  }

  public getItemUniqueKey(item) {
    return item.name;
  }

  public getRecentItems(subKey, limit = null) {
    if (this.persistence) {
      const items = this.persistence.getObject(`recent-items-${subKey}`) || [];

      return limit ? items.slice(0, limit) : items;
    }

    return [];
  }

  public addRecentItem(subKey, item) {
    if (this.persistence) {
      const items = this.getRecentItems(subKey);
      const newItems = uniqBy([item, ...items], (recentItem) => this.getItemUniqueKey(recentItem)).slice(
        0,
        this.getMaxRecentItems(),
      );

      this.persistence.setObject(`recent-items-${subKey}`, newItems);
    }
  }

  public getPrefixLabel() {
    return null;
  }

  public handleLabelClick() {
    return null;
  }

  public isContentSearchable() {
    return true;
  }

  public showCreationControls() {
    return true;
  }

  /** defines if this menu is interactable */
  public canOpenMenu() {
    return true;
  }

  public shouldCloseOnSelect(_detail: any) {
    return true;
  }

  public selectedLabel(): string {
    return ``;
  }

  public selectedIcon(): string {
    return ``;
  }

  public transformSelectedVal(val) {
    return val;
  }

  public handleCloseMenu() {
    return;
  }

  public getSelectedEvent() {
    // depending on whether this is one part of a compound menu
    let selected = this.state.staged?.event || this.attr(`selected`);
    if (selected && selected.event) {
      selected = selected.event;
    }
    return selected;
  }

  public getSelectedEventName() {
    const selected = this.getSelectedEvent();
    return selected && (selected.custom ? selected.name || selected.label : selected.value);
  }

  public getSelectedProperty() {
    // depending on whether this is one part of a compound menu
    let selected = this.state.staged ? this.state.staged.property : this.attr(`selected`);
    if (selected && selected.property) {
      selected = selected.property;
    }
    return selected;
  }

  public getSelectedPropertyName() {
    const selected = this.getSelectedProperty();

    if (!selected) {
      return ``;
    }

    let label =
      selected.label ||
      renameProperty(selected.name || selected.value, {
        dataGroupId: selected.dataGroupId,
        customProperty: selected.customProperty,
      });

    if (selected.unit) {
      label = `${label} (${TimeUnitLabels[selected.unit]})`;
    }

    return label;
  }

  public trackSelection(props, cancelable = true) {
    emitTrackEvent(this, {...props, eventPrefix: null, cancelable});
  }

  public trackEvent(eventName, props = {}, {cancelable = false, prefix = null} = {}) {
    emitTrackEvent(this, {eventName, props, eventPrefix: prefix, cancelable});
  }

  // allows child-menus to run either as query-block children or independently
  get projectId() {
    return this.state.projectId || this.attr(`project-id`);
  }

  get workspaceId() {
    return this.state.workspaceId || this.attr(`workspace-id`);
  }

  get sourceAttrs() {
    return {projectId: this.projectId, workspaceId: this.workspaceId};
  }

  set keydownProxyEl(keydownProxyEl) {
    this.update({keydownProxyEl} as Partial<BaseState & StateT>);
  }

  protected retrieveDataGroups(): Promise<DataGroup[]> {
    const params = this.sourceAttrs;
    return dataGroupsClient.query(params).catch((error) => {
      reporter.error(`Error loading data groups`, error);
      return [];
    });
  }

  // Get tooltip data for the `selected` item
  public getSelectedHoverTooltipData(_ = {}): ContextPanelData {
    return null;
  }

  public prefetchTooltipOptions(): any {
    return null;
  }

  public getSelectedTrackingProps(): Obj {
    return null;
  }

  // Handler for context panel action. Returns boolean indicating if the action was handled.
  public handleTooltipContextAction(_ev: Event): TooltipContextActionResult {
    return null;
  }
}
